import { AbstractControl, FormGroup } from '@angular/forms';

export abstract class BaseRule {
  /**
   * Retrieves the type associated with the implementation of this method.
   *
   * @return {string} The type as a string.
   */
  abstract getType(): string;

  /**
   * Retrieves the name value associated with the implementation of this method.
   *
   * @return {string} The name as a string.
   */
  abstract getName(): string;

  /**
   * Retrieves the description associated with the implementation of this method.
   *
   * @return {string} A string representing the description.
   */
  abstract getDescription(): string;

  /**
   * Retrieves the icon associated with the current object or context.
   *
   * @return {string} The icon identifier or representation.
   */
  abstract getIcon(): string;

  /**
   * Retrieves the editor component associated with this method.
   *
   * @return {any} The editor component instance.
   */
  abstract getEditorComponent(): any;

  /**
   * Updates and modifies a form's data with the provided input.
   *
   * @param {Object} data - The config object containing key-value pairs to update the form.
   * @return {Record<string, unknown>} An object representing the modified form data.
   */
  abstract configToFrom(data: {}): Record<string, unknown>;

  /**
   * Converts a given form object into a configuration object.
   *
   * @param {Object} form - The form object containing key-value pairs to be converted.
   * @return {Record<string, unknown>} A configuration object generated from the form data.
   */
  abstract formToConfig(form: {}): Record<string, unknown>;

  /**
   * Abstract method to create and return a new instance of a FormGroup.
   *
   * @return {FormGroup} A FormGroup instance, which represents a group of form controls.
   */
  abstract createFormGroup(config?: {}): FormGroup;

  abstract getFromControls(): Record<string, AbstractControl>;

  public isConfigDifference(
    originalValue: Record<string, unknown> | undefined | null,
    formValue: Record<string, unknown> | undefined | null,
  ): boolean {
    const isNullOrUndefined = (value: unknown): value is null | undefined => {
      return value === null || value === undefined;
    };

    const isObject = (value: unknown): value is Record<string, unknown> => {
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    };

    const compare = (currentValue: unknown, configValue: unknown): boolean => {
      if (isNullOrUndefined(currentValue) || isNullOrUndefined(configValue)) {
        return false;
      }

      if (Array.isArray(currentValue) && Array.isArray(configValue)) {
        if (currentValue.length !== configValue.length) {
          return true;
        }

        return currentValue.some((value, index) => compare(value, configValue[index]));
      }

      if (isObject(currentValue) && isObject(configValue)) {
        return Object.keys(currentValue)
          .some((key) => {
            return compare(currentValue[key], configValue[key]);
          });
      }

      return currentValue !== configValue;
    };

    if (!originalValue || !formValue) {
      return false;
    }

    return compare(originalValue, formValue);
  }
}